import { Form, Grid, Select, Text, TextField, ToggleButton } from '@approvalmax/ui/src/components';
import { netSuiteConstants } from 'modules/common/constants';
import { useVendorTemplateFieldSettings } from 'pages/requestForm/containers/netsuite/hooks';
import {
    isFieldMandatory,
    isFieldVisible,
} from 'pages/requestForm/containers/netsuite/NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { memo } from 'react';
import { useWatch } from 'react-hook-form';

import { FormValues, VendorType } from '../../NetSuiteVendorCreationPopupContent.types';
import { useCategoriesItems } from './PrimaryInformation.hooks';
import { messages } from './PrimaryInformation.messages';

export const PrimaryInformation = memo(() => {
    const fieldSettings = useVendorTemplateFieldSettings();

    const vendorType = useWatch<FormValues, 'vendorType'>({ name: 'vendorType' });

    const isPerson = vendorType === VendorType.Individual;

    const { categories, isCategoriesFetching, categoriesTrigger, setCategoriesQuery } = useCategoriesItems();

    return (
        <Form.Part>
            <Text font='headline' fontSize='xxsmall' fontWeight='medium' color='midnight80' spacing='20 0'>
                {messages.primaryInformationSection}
            </Text>

            <Grid gap={16}>
                <Grid gap={8} width='50%' padding='0 0 16 0'>
                    <Text font='label' fontSize='small' color='midnight70'>
                        {messages.vendorType}
                    </Text>

                    <ToggleButton.Group name='vendorType'>
                        <ToggleButton width='140px' value={VendorType.Company}>
                            {messages.isCompany}
                        </ToggleButton>

                        <ToggleButton width='140px' value={VendorType.Individual}>
                            {messages.isIndividual}
                        </ToggleButton>
                    </ToggleButton.Group>
                </Grid>

                <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                    {isPerson ? (
                        <>
                            <TextField
                                label={messages.companyName}
                                name='companyName'
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.companyNameMaxLength}
                            />

                            {isFieldVisible(fieldSettings.salutation) && (
                                <TextField
                                    label={messages.salutation}
                                    required={isFieldMandatory(fieldSettings.salutation)}
                                    name='salutation'
                                    placeholder={messages.inputPlaceholder}
                                    maxLength={netSuiteConstants.salutationMaxLength}
                                />
                            )}

                            <TextField
                                label={messages.firstName}
                                required
                                name='firstName'
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.firstNameMaxLength}
                            />

                            <TextField
                                label={messages.middleName}
                                name='middleName'
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.middleNameMaxLength}
                            />

                            <TextField
                                label={messages.lastName}
                                required
                                name='lastName'
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.lastNameMaxLength}
                            />

                            {isFieldVisible(fieldSettings.jobTitle) && (
                                <TextField
                                    label={messages.jobTitle}
                                    required={isFieldMandatory(fieldSettings.jobTitle)}
                                    name='jobTitle'
                                    placeholder={messages.inputPlaceholder}
                                    maxLength={netSuiteConstants.jobTitleMaxLength}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            <TextField
                                label={messages.companyName}
                                required
                                name='companyName'
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.companyNameMaxLength}
                            />

                            {isFieldVisible(fieldSettings.webAddress) && (
                                <TextField
                                    label={messages.webAddress}
                                    required={isFieldMandatory(fieldSettings.webAddress)}
                                    name='webAddress'
                                    placeholder={messages.inputPlaceholder}
                                    maxLength={netSuiteConstants.webAddressMaxLength}
                                />
                            )}
                        </>
                    )}

                    {isFieldVisible(fieldSettings.category) && (
                        <Select
                            label={messages.category}
                            required={isFieldMandatory(fieldSettings.category)}
                            name='categoryId'
                            placeholder={messages.selectPlaceholder}
                            preventSearch
                            autocomplete
                            items={categories}
                            onFocus={categoriesTrigger}
                            onInputChange={setCategoriesQuery}
                            progress={isCategoriesFetching}
                            clearable={!isFieldMandatory(fieldSettings.category)}
                        />
                    )}

                    {isFieldVisible(fieldSettings.comments) && (
                        <TextField
                            label={messages.comments}
                            required={isFieldMandatory(fieldSettings.comments)}
                            name='comments'
                            placeholder={messages.inputPlaceholder}
                            maxLength={netSuiteConstants.commentsMaxLength}
                        />
                    )}
                </Grid>
            </Grid>
        </Form.Part>
    );
});

PrimaryInformation.displayName = 'PrimaryInformation';
